import { Button } from '@mui/material'
import React, { useState } from 'react'
import { useContext } from 'react'
import { useUnmount } from '~/shared/hooks/useMount'
import FullscreenExit from '@mui/icons-material/FullscreenExit'
import Fullscreen from '@mui/icons-material/Fullscreen'

/**
 * For use on Patient/Person charts which are split into a lefthand and righthand side.
 * Context for controlling and displaying the expanded state of the lefthand side
 */
export const ExpandedModeContext = React.createContext<{
  toggleExpandedMode?: () => void
  resetExpandedMode?: () => void
  expandedModeIsActive: boolean
}>({ expandedModeIsActive: false })

export const useExpandedModeControls = () => {
  const [expandedModeIsActive, setExpandedModeIsActive] = useState(false)
  const toggleExpandedMode = () => setExpandedModeIsActive(current => !current)
  const resetExpandedMode = () => setExpandedModeIsActive(false)

  // The props for the expandable component, which should enclose the lefthand side of the screen.
  // `minWidth` is what carries the expanded width: it reaches the Grid item itself, and Grid
  // gives every item `min-width: 0`, so without the floor the item shrinks to fit the row and
  // the lefthand side never widens. The item's inner Box then fills it at `width: 100%`.
  const LefthandGridContainerProps = expandedModeIsActive ? { minWidth: '90vw' } : { xs: 7 }

  return { LefthandGridContainerProps, toggleExpandedMode, resetExpandedMode, expandedModeIsActive }
}

export const ExpandedModeControls = () => {
  const { toggleExpandedMode, expandedModeIsActive, resetExpandedMode } =
    useContext(ExpandedModeContext)

  // Make sure to turn off expanded mode when the component unmounts so that we don't have to
  // click back to find the controls. `useUnmount` holds the callback in a ref, so this stays an
  // unmount-only reset even though the context rebuilds it on every render.
  useUnmount(() => {
    if (resetExpandedMode) resetExpandedMode()
  })

  return (
    <Button
      variant="text"
      size="small"
      color="inherit"
      startIcon={expandedModeIsActive ? <FullscreenExit /> : <Fullscreen />}
      onClick={() => {
        if (toggleExpandedMode) toggleExpandedMode()
      }}
    >
      {expandedModeIsActive ? 'Collapse' : 'Expand'}
    </Button>
  )
}
